import $ from 'jquery';
import LottieAnimationLoader from './lottie-animation-loader';
export default class ContentSyncSliderAnimation {
    $containers = $();
    loader = null;
    players = new Map();
    pendingPlayers = new Map();
    completeListeners = new Map();
    reducedMotion = false;
    activeIndex = -1;
    static init($root) {
        const source = $root.attr('data-animation-source');
        const assetSources = LottieAnimationLoader.parseAssetSources($root.attr('data-animation-assets'));
        const $containers = $root.find('.js__content-sync-slider-animation');
        if (!source || !assetSources || !$containers.length) {
            return null;
        }
        const animation = new ContentSyncSliderAnimation();
        animation.$containers = $containers;
        animation.loader = LottieAnimationLoader.init(source, assetSources);
        animation.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        return animation;
    }
    activate(index) {
        this.activeIndex = index;
        this.players.forEach((player, playerIndex) => {
            if (playerIndex !== index) {
                player.pause();
            }
        });
        void this.ensurePlayer(index).then(player => {
            if (player && this.activeIndex === index) {
                this.play(index, player);
            }
        });
        [index - 1, index + 1].forEach(neighbourIndex => {
            if (neighbourIndex >= 0 && neighbourIndex < this.$containers.length) {
                void this.ensurePlayer(neighbourIndex);
            }
        });
    }
    deactivate() {
        this.activeIndex = -1;
        this.players.forEach(player => player.pause());
        this.completeListeners.forEach(removeListener => removeListener());
        this.completeListeners.clear();
    }
    static parseSegment(value) {
        if (!value) {
            return null;
        }
        try {
            const parsed = JSON.parse(value);
            if (Number.isFinite(parsed.still)) {
                return { still: parsed.still };
            }
            if (!Array.isArray(parsed.loop) ||
                parsed.loop.length !== 2 ||
                !parsed.loop.every(Number.isFinite) ||
                parsed.loop[1] <= parsed.loop[0] ||
                (parsed.start !== undefined && !Number.isFinite(parsed.start))) {
                return null;
            }
            return {
                start: parsed.start,
                loop: parsed.loop,
            };
        }
        catch (_error) {
            return null;
        }
    }
    ensurePlayer(index) {
        const existingPlayer = this.players.get(index);
        if (existingPlayer) {
            return Promise.resolve(existingPlayer);
        }
        const pendingPlayer = this.pendingPlayers.get(index);
        if (pendingPlayer) {
            return pendingPlayer;
        }
        const $container = this.$containers.eq(index);
        const container = $container.get(0);
        const segment = ContentSyncSliderAnimation.parseSegment($container.attr('data-animation-segment'));
        if (!container || !segment || !this.loader) {
            return Promise.resolve(null);
        }
        const playerRequest = this.loader
            .load(container)
            .then(player => {
            this.players.set(index, player);
            $container.addClass('is-loaded');
            this.showInitialFrame(player, segment);
            return player;
        })
            .catch(() => null)
            .finally(() => this.pendingPlayers.delete(index));
        this.pendingPlayers.set(index, playerRequest);
        return playerRequest;
    }
    showInitialFrame(player, segment) {
        const initialTime = this.reducedMotion
            ? segment.still ?? segment.loop?.[0]
            : segment.still ?? segment.start ?? segment.loop?.[0];
        if (initialTime !== undefined) {
            player.goToAndStop(initialTime * player.frameRate, true);
        }
    }
    play(index, player) {
        this.completeListeners.get(index)?.();
        this.completeListeners.delete(index);
        player.setLoop(false);
        player.pause();
        const segment = ContentSyncSliderAnimation.parseSegment(this.$containers.eq(index).attr('data-animation-segment'));
        if (!segment) {
            return;
        }
        if (this.reducedMotion || segment.still !== undefined) {
            this.showInitialFrame(player, segment);
            return;
        }
        const [loopStart, loopEnd] = segment.loop;
        const start = segment.start ?? loopStart;
        const loopFrames = [loopStart * player.frameRate, loopEnd * player.frameRate];
        if (start === loopStart) {
            player.setLoop(true);
            player.playSegments(loopFrames, true);
            return;
        }
        const removeCompleteListener = player.addEventListener('complete', () => {
            if (this.activeIndex !== index) {
                return;
            }
            removeCompleteListener();
            this.completeListeners.delete(index);
            player.setLoop(true);
            player.playSegments(loopFrames, true);
        });
        this.completeListeners.set(index, removeCompleteListener);
        player.playSegments([start * player.frameRate, loopEnd * player.frameRate], true);
    }
}
